import type {
  Dimensions,
  FitViewOptions,
  Node,
  ReactFlowState,
  Viewport,
  XYPosition,
} from '@xyflow/react'

const PANE_MARGIN_PX = 16
const ADD_STEP = 20
// The lowest zoom of the editor canvas. A large tree still fits whole.
export const EDITOR_MIN_ZOOM = 0.25
const ZOOM_OUT_STEP = 0.8
// Flow units of a node's header, which names the node and must stay readable.
const TITLE_HEIGHT = 44
const SAMPLES = 20
const BELOW_GAP = 24

// The pane in screen pixels, of which the top `coveredTop` pixels lie under
// the editor header.
export type VisiblePane = Pick<
  ReactFlowState,
  'width' | 'height' | 'transform' | 'minZoom'
> & {
  coveredTop: number
}

export function visiblePane(
  { width, height, transform, minZoom, domNode }: ReactFlowState,
  header: HTMLElement
): VisiblePane {
  const coveredTop =
    header.getBoundingClientRect().bottom - domNode!.getBoundingClientRect().top
  return {
    width,
    height,
    transform,
    minZoom,
    coveredTop: Math.max(0, coveredTop),
  }
}

// A box in flow units: a node, or the part of the pane the operator can see.
type Box = XYPosition & Dimensions

// The pane below the header and inside the margin, in flow units.
function freeArea({
  width,
  height,
  coveredTop,
  transform: [tx, ty, zoom],
}: VisiblePane): Box {
  const x = (PANE_MARGIN_PX - tx) / zoom
  const y = (coveredTop + PANE_MARGIN_PX - ty) / zoom
  return {
    x,
    y,
    width: (width - PANE_MARGIN_PX - tx) / zoom - x,
    height: (height - PANE_MARGIN_PX - ty) / zoom - y,
  }
}

// The viewport one step further out, with the middle of the free area fixed,
// so every node on screen stays on screen.
function zoomedOut(pane: VisiblePane): Viewport {
  const [tx, ty, zoom] = pane.transform
  const next = Math.max(pane.minZoom, zoom * ZOOM_OUT_STEP)
  const cx = pane.width / 2
  const cy = (pane.coveredTop + pane.height) / 2
  return {
    x: cx - ((cx - tx) / zoom) * next,
    y: cy - ((cy - ty) / zoom) * next,
    zoom: next,
  }
}

const boxOf = (node: Node): Box => ({
  ...node.position,
  width: node.measured?.width ?? 0,
  height: node.measured?.height ?? 0,
})

const inside = (b: Box, px: number, py: number) =>
  px >= b.x && px <= b.x + b.width && py >= b.y && py <= b.y + b.height

// An earlier node, sampled on a grid. `open` holds the sample points that no
// other earlier node hides, so a new node's cover adds to what is hidden
// already instead of being judged alone.
type Sampled = { box: Box; open: XYPosition[]; hiddenBefore: number }

function sample(box: Box, boxes: Box[]): Sampled {
  const open: XYPosition[] = []
  for (let i = 0; i < SAMPLES; i++) {
    for (let j = 0; j < SAMPLES; j++) {
      const p = {
        x: box.x + ((i + 0.5) / SAMPLES) * box.width,
        y: box.y + ((j + 0.5) / SAMPLES) * box.height,
      }
      if (!boxes.some(o => o !== box && inside(o, p.x, p.y))) open.push(p)
    }
  }
  return { box, open, hiddenBefore: SAMPLES * SAMPLES - open.length }
}

// The share of `lower` that `upper` hides, for the title strip.
function hiddenShare(upper: Box, lower: Box): number {
  const w =
    Math.min(upper.x + upper.width, lower.x + lower.width) -
    Math.max(upper.x, lower.x)
  const h =
    Math.min(upper.y + upper.height, lower.y + lower.height) -
    Math.max(upper.y, lower.y)
  return w > 0 && h > 0 ? (w * h) / (lower.width * lower.height) : 0
}

// `from` to `to` in steps, with `to` itself and the midpoint.
function steps(from: number, to: number): number[] {
  const out = [(from + to) / 2, to]
  for (let v = from; v < to; v += ADD_STEP) out.push(v)
  return out
}

// Every top-left corner that keeps the node inside the free area. A node
// wider than the area keeps its right edge at the margin, where its title
// sits in this RTL UI. A node taller than the area keeps its top edge at the
// margin, or drops at most to the middle of the area.
function candidates(free: Box, size: Dimensions): XYPosition[] {
  const right = free.x + free.width - size.width
  const xs = size.width <= free.width ? steps(free.x, right) : [right]
  const ys =
    size.height <= free.height
      ? steps(free.y, free.y + free.height - size.height)
      : steps(free.y, free.y + free.height / 2)
  return xs.flatMap(x => ys.map(y => ({ x, y })))
}

// The spot in the free area that covers no header and hides at most half of
// every node, counting what other nodes hide already. If no such spot exists,
// the one with the least total hidden share, then the one nearest the middle,
// so a lone node is centered.
function bestSpot(free: Box, size: Dimensions, boxes: Box[]) {
  const middle = {
    x: free.x + (free.width - size.width) / 2,
    y: free.y + (free.height - size.height) / 2,
  }
  const sampled = boxes.map(b => sample(b, boxes))
  const score = (at: XYPosition) => {
    const rect = { ...at, ...size }
    let worst = 0
    let total = 0
    let titleHit = false
    for (const s of sampled) {
      const added = s.open.filter(p => inside(rect, p.x, p.y)).length
      const share = (s.hiddenBefore + added) / (SAMPLES * SAMPLES)
      if (added > 0) {
        worst = Math.max(worst, share)
        total += added / (SAMPLES * SAMPLES)
      }
      if (hiddenShare(rect, { ...s.box, height: TITLE_HEIGHT }) > 0) {
        titleHit = true
      }
    }
    return [
      titleHit ? 1 : worst > 0.5 ? worst : 0,
      total,
      (at.x - middle.x) ** 2 + (at.y - middle.y) ** 2,
    ]
  }
  const better = (a: number[], b: number[]) => {
    const i = a.findIndex((v, k) => v !== b[k])
    return i >= 0 && a[i] < b[i]
  }
  let best: XYPosition | undefined
  let bestScore: number[] = []
  for (const at of candidates(free, size)) {
    const s = score(at)
    if (!best || better(s, bestScore)) [best, bestScore] = [at, s]
  }
  return { at: best!, overHalf: bestScore[0] }
}

export type Placement = {
  position: XYPosition
  // Set when the pane had to zoom out to make room.
  viewport?: Viewport
}

// Where a node of `size` added from the side panel goes. While the node is
// wider or taller than the visible part of the pane, or every spot in it
// would cover a node's header or hide more than half of a node, the pane zooms
// out a step, down to the flow's minimum zoom. At the minimum the node goes
// below every other node and the pane pans to it, so it buries nothing.
export function placeInPane(
  pane: VisiblePane,
  size: Dimensions,
  others: Node[]
): Placement {
  const boxes = others.map(boxOf)
  let viewport: Viewport | undefined
  for (;;) {
    const current: VisiblePane = viewport
      ? { ...pane, transform: [viewport.x, viewport.y, viewport.zoom] }
      : pane
    const free = freeArea(current)
    const { at, overHalf } = bestSpot(free, size, boxes)
    const tooBig = size.width > free.width || size.height > free.height
    if (overHalf === 0 && !tooBig) return { position: at, viewport }
    if (current.transform[2] > pane.minZoom) {
      viewport = zoomedOut(current)
      continue
    }
    if (overHalf === 0 || boxes.length === 0) return { position: at, viewport }
    return belowAll(current, free, size, boxes)
  }
}

function belowAll(
  pane: VisiblePane,
  free: Box,
  size: Dimensions,
  boxes: Box[]
): Placement {
  const [tx, , zoom] = pane.transform
  const x = size.width <= free.width ? free.x + free.width - size.width : free.x
  const y = Math.max(...boxes.map(b => b.y + b.height)) + BELOW_GAP
  const top = PANE_MARGIN_PX + pane.coveredTop
  return {
    position: { x, y },
    viewport: { x: tx, y: top - y * zoom, zoom },
  }
}

// The view a tree opens in: the whole tree in the part of the pane below the
// header, never magnified past its natural size.
export function fitViewOptions(coveredTop: number): FitViewOptions {
  const margin = `${PANE_MARGIN_PX}px`
  return {
    padding: {
      top: `${coveredTop + PANE_MARGIN_PX}px`,
      right: margin,
      bottom: margin,
      left: margin,
    },
    minZoom: EDITOR_MIN_ZOOM,
    maxZoom: 1,
    duration: 0,
  }
}
